/* eslint-disable @typescript-eslint/member-ordering */
import type { IAttachmentClassify } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { EShowType } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import type { CSSProperties, ReactElement } from 'react';

export interface Uploader {
    upload: (params: {
        file: File;
        taskId: string;
        onSuccess: (id: string, url: string) => void;
        onError: (id: string) => void;
        onProcess: (id: string, processData: any) => void;
    }) => void;
}

/**
 * 附件信息
 */
export interface AttachmentInfo {
    status?: string | undefined; // 上传状态
    id?: string;  // 上传id，上传过程中存在
    name?: string;  // 附件名称
    size?: number;  // 附件尺寸，单位是字节数
    type?: string;  // 附件类型
    filetype?: string;  // 附件文件类型
    objKey?: string;  // 附件key，已上传的附件唯一标识
    instanceKey?: string; // 附件场景可能存在有复制粘贴的情况, 此时 objKey 是一样的, 为了区分实例, 补充一个 instanceKey
    classify?: IAttachmentClassify; // 附件分类
    showType?: EShowType;
    styles?: {
        // 支持覆盖.attachment-wrapper, .icon-wrapper, .text-link, .file-icon, .rc-progress-circle
        [selector: string]: CSSProperties;
    };
    posterInfo?: {
        url?: string; // 视频附件图片附件的封面
        aspectRatio?: number; // 视频附件图片附件的封面宽高比
    }
}

/**
 * 附件元素绑定信息
 */
export interface AttachmentElement {
    attachment: AttachmentInfo;  // 附件信息
    slideId: string;  // 所属slide id
    shapeId: string;  // 附件承载的shape id
    shapeUniqueId?: string; // sdk唯一id
}

export enum EPermission {
    VISITOR = 'visitor', // 访客
    VIEW = 'view', // 可查看
    EDIT = 'edit', // 可编辑
    OWNER = 'owner', // 创建人
}

export interface AttachmentProps {
    attachment: AttachmentInfo;
    padId: string;
    isCircleAudio?: boolean;
    isDisableDownload?: boolean;
    isDisableVideoResolution?: boolean;
    className?: string;
    reUpload?(id: string): void;
    renderAttachmentContent?(attachment: AttachmentInfo): ReactElement;
    // 滚动监听，主要用于dui的dropdown组件的滚动时消失
    addScrollEventListener?: Function;
    // 取消滚动监听
    removeScrollEventListener?: Function;
    // 组件调用收起软键盘
    requestBlur?: Function
    // 音视频兼容原有参数
    audioAndVideoParams?: any;
    onImageFullScreenChange?: (isFullScreen: boolean) => void; // 是否图片全屏
    // 回调传递图片附件的 src 地址
    getImgSrc?: (src: string) => void;
    // 高级权限
    canCopy?: boolean;
    // 直接展示 video 播放器组件，不优先展示封面图
    directShowVideoPlayer?: boolean;
    // 隐藏 video 的头部
    hideVideoHeader?: boolean;
    // 点击回调
    onClick?: (attachment: AttachmentInfo) => void;
    // 订阅全屏预览状态，囊括所有格式的文件是否调用了全屏预览。
    onFullScreenChange?: (isFullScreen: boolean) => void;
    // video 的封面图
    videoPlacerHolderPic?: string;
}

export interface AttachmentToolbarProps {
    attachment?: AttachmentInfo;
    padId?: string;
    className?: string;
    permission?: EPermission; // 用户权限
    isDisableDownload?: boolean; // 是否禁止下载
    hideShowTypeList?: EShowType[]; // 切换形态下拉按钮黑名单
    onChangeShowType?: (showType: EShowType) => void; //  工具栏中改变showType的回调
    onDelete?: () => void; // 工具栏中删除按钮点击回调
    onFullScreenChange?: (isFullScreen: boolean) => void;
}

/**
 * 上传配置新
 */
export interface UploadConfig {
    padId?: string;  // 文档id
    maxUploadNumber?: number; // 最大同时上传数
    uploader?: Uploader;
    multiple?: boolean; // 是否使用多文件上传
}

export interface AttachmentRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export type MetaInfo = {
    showType: AttachmentInfo['showType'];
    width: number;
    height: number;
};

/**
 * SuperPlayer 应该是 @tencent/super-player 默认导出类的类型
 * 附件中用到的视频实例就是这个类型，理论上来说这个类型应该由 dc 组件提供，但是 dc 现在无法提供
 * 为了避免引入多余的依赖，就暂时把这个类型设置为 any
 */
export type SuperPlayer = any;
